import { ref, reactive } from 'vue'
import { useStoreReviews } from '@/stores/storeReviews'
import { useStoreTitlesUser } from '@/stores/storeTitlesUser'
import { useStoreLists } from '@/stores/storeLists'
import { useStoreTitleLogs } from '@/stores/storeTitleLogs'

let storeReviews = null
// useStoreReviews()

function toLocalDateString(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function getStore() {
  if (storeReviews === null) {
    storeReviews = useStoreReviews()
  }
  return storeReviews
}

export function useReviewForm() {
  const selectedTMDBTitle = ref(null)
  const existingReview = ref(null)

  // Draft state
  const draftId = ref(null)
  const draftTitleId = ref(null)
  const draftPromise = ref(null)
  const isDraftReady = ref(false)
  const draftFailed = ref(false)

  // Draft resume state
  const foundDraft = ref(null)
  const draftCheckDone = ref(false)
  const didResumeDraft = ref(false)

  const initReview = {
    title: '',
    tmdb_id: null,
    tmdb_type: '',
    review_type: 'movie',
    title_title: '',
    title_subtitle: '',
    tmdb_poster_path: '',
    tmdb_backdrop_path: '',
    score_a: 5,
    score_b: 5,
    score_c: 5,
    score_d: 5,
    score_e: 5,
    score_f: 5,
    sentiment: 3,
    image_path: null,
    image_transform: null,
    watched_at: null,
    when: toLocalDateString(new Date()),
    tags: '',
    highlight: '',
    lowlight: '',
    note: '',
    comments: '',
    tv_season: null,
    tv_episode: null,
    watched_on: null,
    club_id: null
  }

  let review = reactive({})

  /**
   * Create a draft review in the background.
   * Call this as soon as the title is selected (form mount).
   */
  const createDraft = () => {
    // Only create drafts for new reviews, not edits
    if (review.id) return
    // Don't create if we already have a draft in progress or completed
    if (draftPromise.value) return

    const store = getStore()

    const promise = store
      .createDraft({
        tmdb_id: review.tmdb_id,
        tmdb_type: review.tmdb_type,
        title_title: review.title_title,
        review_type: review.review_type,
      })
      .then((result) => {
        draftId.value = result.review.id
        draftTitleId.value = result.review.title_id
        isDraftReady.value = true
        return result
      })
      .catch((err) => {
        console.warn('Draft creation failed, will fall back to full create:', err)
        draftFailed.value = true
        draftId.value = null
        isDraftReady.value = false
      })

    draftPromise.value = promise
  }

  /**
   * Save draft progress when user closes the form.
   * Fire-and-forget — saves current form state so it can be resumed later.
   */
  const saveDraftProgress = () => {
    if (draftId.value && isDraftReady.value) {
      const store = getStore()
      store.updateDraft(draftId.value, {
        score_a: review.score_a,
        score_b: review.score_b,
        score_c: review.score_c,
        score_d: review.score_d,
        score_e: review.score_e,
        score_f: review.score_f,
        sentiment: review.sentiment,
        tags: review.tags,
        highlight: review.highlight,
        lowlight: review.lowlight,
        note: review.note,
        when: review.when,
        watched_on: review.watched_on,
        image_path: review.image_path,
        image_transform: review.image_transform,
        review_type: review.review_type,
        tv_season: review.tv_season,
        tv_episode: review.tv_episode,
        title_subtitle: review.title_subtitle,
        club_id: review.club_id,
      }).catch((err) => {
        console.warn('Failed to save draft progress:', err)
      })
    }
  }

  /**
   * Check for an existing draft and auto-resume it if found.
   * Returns the draft row or null.
   */
  const checkForExistingDraft = async (tmdbId, tmdbType) => {
    const store = getStore()
    const draft = await store.findDraftForTitle(tmdbId, tmdbType)
    draftCheckDone.value = true

    if (draft) {
      foundDraft.value = draft
      resumeDraft()
      return draft
    }

    return null
  }

  /**
   * Resume a found draft — loads its saved data into the form.
   */
  const resumeDraft = () => {
    if (!foundDraft.value) return

    const draft = foundDraft.value
    // Load saved scores/meta into the reactive review object
    const fieldsToRestore = [
      'score_a', 'score_b', 'score_c', 'score_d', 'score_e', 'score_f',
      'sentiment', 'tags', 'highlight', 'lowlight', 'note', 'when',
      'watched_on', 'image_path', 'image_transform',
      'review_type', 'tv_season', 'tv_episode', 'title_subtitle',
      'club_id',
    ]

    for (const field of fieldsToRestore) {
      if (draft[field] !== undefined && draft[field] !== null) {
        review[field] = draft[field]
      }
    }

    // Set draft state from the existing draft
    draftId.value = draft.id
    draftTitleId.value = draft.title_id
    isDraftReady.value = true
    draftPromise.value = Promise.resolve()
    didResumeDraft.value = true

    foundDraft.value = null
  }

  /**
   * Discard the resumed draft and start fresh.
   */
  const discardAndRestart = async () => {
    if (draftId.value) {
      const store = getStore()
      store.deleteDraft(draftId.value).catch((err) => {
        console.warn('Failed to delete old draft:', err)
      })
    }
    // Reset draft state so createDraft can run
    draftId.value = null
    draftTitleId.value = null
    draftPromise.value = null
    isDraftReady.value = false
    didResumeDraft.value = false

    // Reset form to defaults
    Object.assign(review, {
      score_a: 5, score_b: 5, score_c: 5,
      score_d: 5, score_e: 5, score_f: 5,
      sentiment: 3, tags: '', highlight: '', lowlight: '', note: '',
      image_path: null, image_transform: null,
      watched_on: null, club_id: null,
    })

    createDraft()
  }

  const handleSave = async () => {
    const store = getStore()

    // Edit mode — use existing update flow
    if (review.id) {
      const result = await store.updateReview(review)
      return result
    }

    // New review: try draft-publish flow
    if (!draftFailed.value && draftPromise.value) {
      // Wait for draft if still creating
      if (!isDraftReady.value) {
        await draftPromise.value
      }

      // If draft succeeded, publish it
      if (draftId.value) {
        const result = await store.publishDraft(draftId.value, review)
        // Clear draft state so cleanup doesn't delete the published review
        draftId.value = null
        isDraftReady.value = false
        draftPromise.value = null

        // Fire-and-forget: refresh client state for auto-marked-as-seen
        const titleId = result?.review?.title_id || draftTitleId.value
        refreshPostCreateState(titleId)

        return result
      }
    }

    // Fallback: full create flow (draft failed or was never started)
    const result = await store.addReview(review)

    if (result) {
      const titleId = result?.review?.title_id
      refreshPostCreateState(titleId)
    }

    return result
  }

  /**
   * Fire-and-forget client state refreshes after review creation.
   * These run in the background — user navigates immediately.
   */
  const refreshPostCreateState = (titleId) => {
    const store = getStore()
    store.fetchUserReviews()

    const storeTitleLogs = useStoreTitleLogs()
    if (titleId) {
      storeTitleLogs.fetchLogsForTitle(titleId)
    } else {
      storeTitleLogs.fetchRecentLogs()
    }

    const storeTitlesUser = useStoreTitlesUser()
    if (titleId) {
      storeTitlesUser.refreshInfoByTId(titleId)
    } else {
      storeTitlesUser.fetchInfoByTitle(
        { id: review.tmdb_id, type: review.tmdb_type },
        true
      )
    }

    const storeLists = useStoreLists()
    const watchlist = storeLists.getSystemListByType('watchlist')
    if (watchlist) {
      const watchlistItem = storeLists.items.find(
        (i) =>
          i.list_id === watchlist.id &&
          (i.title_id === titleId ||
            (i.titles?.tmdb_id == review.tmdb_id &&
              i.titles?.tmdb_type == review.tmdb_type))
      )
      if (watchlistItem) {
        storeLists.removeItem(watchlistItem.id)
      }
    }

    storeLists.fetchSystemListByType('watchlist', true)
    storeLists.fetchSystemListByType('seen', true)
  }

  const setReviewForEdit = (existingReview) => {
    setReviewForNew()
    // Assign only keys that exist in initReview
    Object.keys(initReview).forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(existingReview, key)) {
        review[key] = existingReview[key]
      }
    })

    // Also assign the id
    review.id = existingReview.id
  }

  const setReviewForNew = () => {
    Object.assign(review, initReview)
    // Reset draft state
    draftId.value = null
    draftTitleId.value = null
    draftPromise.value = null
    isDraftReady.value = false
    draftFailed.value = false
  }

  const selectTitleForReview = (tmdbTitle, reviewType, tvSeason, tvEpisode) => {
    selectedTMDBTitle.value = tmdbTitle

    review.title = tmdbTitle.title
    review.tmdb_id = tmdbTitle.id
    review.tmdb_type = tmdbTitle.media_type
    review.title_title =
      tmdbTitle?.title ||
      tmdbTitle?.name ||
      tmdbTitle?.original_title ||
      tmdbTitle?.original_name
    review.tmdb_poster_path = tmdbTitle.poster_path
    review.tmdb_backdrop_path = tmdbTitle.backdrop_path

    if (reviewType) {
      review.review_type = reviewType

      if (reviewType === 'tv_season') {
        review.tv_season = tvSeason
      }

      if (reviewType === 'tv_episode') {
        review.tv_episode = tvEpisode
        review.tv_season = tvSeason
      }
    }
    if (review.review_type === null) {
      if (review.tmdb_type === 'tv') {
        review.review_type = 'tv_full'
      }
      if (review.tmdb_type === 'movie') {
        review.review_type = 'movie_full'
      }
    }
  }

  const sentimentOptions = [
    { label: 'sad', value: 1, path: '/sentiment/sad.svg' },
    { label: 'whatever', value: 2, path: '/sentiment/whatever.svg' },
    { label: 'meh', value: 3, path: '/sentiment/meh.svg' },
    { label: 'happy', value: 4, path: '/sentiment/happy.svg' },
    { label: 'delighted', value: 5, path: '/sentiment/delighted.svg' }
  ]

  return {
    selectedTMDBTitle,
    review,
    handleSave,
    selectTitleForReview,
    sentimentOptions,
    setReviewForEdit,
    setReviewForNew,
    // Draft lifecycle
    createDraft,
    saveDraftProgress,
    draftId,
    isDraftReady,
    draftFailed,
    // Draft resume
    checkForExistingDraft,
    discardAndRestart,
    didResumeDraft,
    draftCheckDone
  }
}
